import {Component} from '@angular/core';
import {ActionsSubject, Store} from '@ngrx/store';
import {AbstractWidgetDirective} from '../../abstract-widget.directive';
import {UiWidget, WidgetConfiguration} from '@pt/pt-models';
import {TranslateService} from '@ngx-translate/core';
import {WidgetDialogService} from '../../../services/widget-dialog.service';
import {AttachmentsService, QueryService} from '@pt/api-client';
import {NgxPermissionsService} from 'ngx-permissions';
import {WidgetCheckboxV0Utils} from '../../widget-checkbox/v0/widget-checkbox-v0.utils';
import { Attachment } from 'it/primatoo/openapi/api/org/schemas/common/Attachment';

@Component({
  selector: 'pt-widget-attachment-v0',
  template: `
    <div class='position-relative' *ngIf="internalStatus.enabled">

      <pt-widget-disabled
        *ngIf='internalStatus.isDisabled'
        [showLoader]="internalStatus.viewMode === 'LOADING'">
      </pt-widget-disabled>

      <pt-widget-header
        class='w-100'
        [label]='widget?.label'
        [help]='widget?.help'

        [comment]="comment"
        [unsavedComment]="unsavedComment"
        [showCancelButton]='internalStatus.showCancelButton'
        [showCommentButton]='internalStatus.showCommentButton'
        [showEditButton]='internalStatus.showEditButton'
        [showReloadButton]='internalStatus.showReloadButton'
        [showSaveButton]='internalStatus.showSaveButton'
        [showOptButton]='internalStatus.showOptButton'

        [disabledCancelButton]='internalStatus.disabledCancelButton'
        [disabledCommentButton]='internalStatus.disabledCommentButton'
        [disabledEditButton]='internalStatus.disabledEditButton'
        [disabledOptButton]='internalStatus.disabledOptButton'
        [disabledReloadButton]='internalStatus.disabledReloadButton'
        [disabledSaveButton]='internalStatus.disabledSaveButton'
        [loading]='internalStatus.isLoading'

        (clickSaveWidget)='saveWidget(false)'
        (clickResetWidget)='resetWidget(widget)'
        (clickEditWidget)='dispatchEditState()'
        (clickReloadWidget)='reloadWidget(widget)'
        (clickCreateOpt)='createOptWidget()'
        (clickCommentWidget)='clickCommentWidget()'>
      </pt-widget-header>

      <ng-container *ngIf='!internalStatus.showOptButton'>

        <pt-widget-attachment-editor-v0
          [data]='localEditableData'
          [noDataLabel]="noDataLabel"
          [show]="internalStatus.viewMode === 'EDIT' || internalStatus.viewMode === 'LOADING'"
          (clickEditWidget)='dispatchEditState()'
          (clickResetWidget)='resetWidget(widget)'
          (currentValue)='setCurrentValue($event)'
          (downloadAttachment)='downloadAttachment($event)'
        ></pt-widget-attachment-editor-v0>

        <pt-widget-attachment-preview-v0
          *ngIf="internalStatus.viewMode === 'PREVIEW'"
          [noDataLabel]="noDataLabel"
          [preview]='previewData'
          [loading]='internalStatus.isLoading'
          (downloadAttachment)='downloadAttachment($event)'
        ></pt-widget-attachment-preview-v0>

        <pt-widget-error
          *ngIf="internalStatus.viewMode === 'ERROR'"
          [errorMessage]='internalStatus.errorMessage | async'>
        </pt-widget-error>

        <pt-widget-footer>
        </pt-widget-footer>
      </ng-container>
    </div>
  `
})
export class WidgetAttachmentV0Component extends AbstractWidgetDirective<Attachment[]> {

  public localEditableData: Attachment[];

  private currentValue: Array<string>;

  constructor(public store: Store,
              public actionsSubject: ActionsSubject,
              public translateService: TranslateService,
              public widgetDialogService: WidgetDialogService,
              public service: QueryService,
              private attachmentsService: AttachmentsService,
              public permissionService: NgxPermissionsService) {
    super(store, actionsSubject, translateService, widgetDialogService, permissionService);
  }

  loadWidget(widget: UiWidget): void {
    this.dispatchInitState();
    this.currentValue = [];
    if (widget.value && widget.value.length > 0) {

      this.currentValue = widget.value ? widget.value : null;
      this.attachmentsService.getAttachmentByUuids(
        widget.value
      ).subscribe((response) => {
        this.localEditableData = response ? response : [];
        this.currentValue = this.localEditableData.map(m => m.uuid);
        this.checkValidity();
        this.valueController(widget, response);

        this.dispatchCompleteState(this.localEditableData);
      }, () => {

        this.valueController(widget, []);

        this.localEditableData = [];
        this.currentValue = this.localEditableData.map(m => m.uuid);
        this.checkValidity();

        this.dispatchErrorState();
      });

    } else {
      this.localEditableData = [];
      this.currentValue = this.localEditableData.map(m => m.uuid);
      this.checkValidity();
      this.dispatchCompleteState(this.localEditableData);
    }
  }

  saveWidget(ignoreTrigger?: boolean): void {
    const widget = JSON.parse(JSON.stringify(this.widget));
    widget.value = this.currentValue;
    widget.comment = this.comment;
    this.checkValidity();
    this.completeSaveWidget(widget, ignoreTrigger);
  }

  setCurrentValue(value: Array<string> | undefined) {
    this.currentValue = value;
    this.checkValidity();
    if (this.internalStatus.isAutoSave && !this.internalStatus.isLoading && this.internalStatus.isDraft) {
      this.saveWidget();
    }
  }

  configuration(): WidgetConfiguration {
    return {supportAutoSave: true, supportValidation: false};
  }

  downloadAttachment(attachment: Attachment) {
    this.attachmentsService.downloadAttachment(
      attachment,
      attachment.attachmentType
    );
  }

  private checkValidity() {
    this.isValid$.next(true);
  }

  private valueController(widget: UiWidget, data: Attachment[]) {

    const widgetValue: string[] = widget?.value ? widget?.value : [];
    const validValues: string[] = data ? data.map(d => d.uuid) : [];
    const validValue = WidgetCheckboxV0Utils.getValidValue(widgetValue, validValues);
    if (validValue?.length !== widgetValue?.length) {
      const updatedWidget = JSON.parse(JSON.stringify(widget));
      updatedWidget.value = validValue;
      this.sendWidgetNotificationInvalidQueryResponseInvalidValue(updatedWidget);
    }
  }
}
